import type { AuditEntryDTO } from '@propmgt/shared';
import { formatDateTime } from '../../lib/date';
import { formatCents } from '../../lib/money';

/**
 * 0.26.0 — renders audit / activity entries (owner-only views). The human label is shown; the raw
 * action code is on hover (`title`) and in a visually-hidden span for screen readers. Details are the
 * server's whitelisted, redacted projection — this component never sees a raw diff.
 */

const RELATION: Record<string, string> = {
  by: 'Did it',
  about: 'About them',
  both: 'Did it (about themselves)',
  session: 'Sign-in',
  payment: 'Payment',
};

function detailText(k: string, v: string | number | boolean | null): string {
  if (v === null) return `${k}: —`;
  if (typeof v === 'number' && /cents$/i.test(k)) return `${k.replace(/Cents$/, '')}: ${formatCents(v)}`;
  if (typeof v === 'boolean') return `${k}: ${v ? 'yes' : 'no'}`;
  if (typeof v === 'string' && /At$/.test(k)) return `${k}: ${formatDateTime(v)}`;
  return `${k}: ${String(v)}`;
}

export function AuditEntries({ items, showRelation = false }: { items: AuditEntryDTO[]; showRelation?: boolean }) {
  if (items.length === 0) return <p className="muted">No activity found.</p>;
  return (
    <div className="table-wrap card" style={{ padding: 0 }}>
      <table>
        <caption className="visually-hidden">Activity</caption>
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">What</th>
            <th scope="col">Who</th>
            {showRelation && <th scope="col">Relation</th>}
            <th scope="col">Details</th>
            <th scope="col">Where from</th>
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.key}>
              <td>{formatDateTime(e.at)}</td>
              <td>
                <span title={e.action}>{e.label}</span>
                <span className="visually-hidden"> ({e.action})</span>
                {e.entityType && e.entityType !== 'session' && (
                  <div className="muted" style={{ fontSize: '0.8rem' }}>
                    {e.entityType.replace(/_/g, ' ')}
                    {e.entityId ? ` ${e.entityId.slice(0, 12)}` : ''}
                  </div>
                )}
              </td>
              <td>
                {e.actorLabel ?? (e.actorType === 'system' ? 'System' : '—')}
                {e.actorType && e.actorType !== 'user' && e.actorType !== 'system' && (
                  <span className="muted"> ({e.actorType})</span>
                )}
              </td>
              {showRelation && <td>{e.relation ? (RELATION[e.relation] ?? e.relation) : '—'}</td>}
              <td>
                {Object.entries(e.details).length === 0 && e.changedFields.length === 0 ? (
                  <span className="muted">—</span>
                ) : (
                  <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                    {Object.entries(e.details).map(([k, v]) => (
                      <li key={k}>{detailText(k, v)}</li>
                    ))}
                    {e.changedFields.length > 0 && <li>Changed: {e.changedFields.join(', ')}</li>}
                  </ul>
                )}
              </td>
              <td>
                {e.ipPrefix || e.device ? (
                  <>
                    {e.device ?? ''}
                    {e.ipPrefix ? <div className="muted">{e.ipPrefix}</div> : null}
                  </>
                ) : (
                  <span className="muted">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Save a Blob as a file (the .xlsx exports). */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
